import { MegaMenuItem } from '@design-system/components/m-mega-menu';
import { getNextToLastUriSegment } from '@portal/app/shared/helpers/helpers';
import { UserModel, UserType } from '@portal/app/shared/models/userModel';
import { DisplayDashboard } from '@portal/app/shared/types';
import { NavbarConstants } from '@portal/app/shared/types/navigation';
import { MenuItem } from 'primeng/api';

import {
  MORE_META,
  SETTINGS_META,
  reportIssueItem,
  supportBaseItems
} from './navbar.config';

/** A brand identified by its client/brand pair — the unit stored for MRU recents. */
export interface BrandKey {
  clientId: number;
  brandId: number;
}

/** Per-user MRU recents, keyed by userId. Each list is most-recent-first, max 3. */
export type RecentBrandsMap = Record<string, BrandKey[]>;

const MAX_RECENTS = 3;

/**
 * Record an explicit brand selection for a user, MRU-first. Drops any existing
 * entry for the same brand, unshifts it to the front, and caps the list at 3.
 * Returns a new map (does not mutate the input).
 */
export function pushRecent(
  map: RecentBrandsMap,
  userId: string,
  key: BrandKey
): RecentBrandsMap {
  const current = map[userId] ?? [];
  const deduped = current.filter(
    (k) => k.clientId !== key.clientId || k.brandId !== key.brandId
  );
  return {
    ...map,
    [userId]: [key, ...deduped].slice(0, MAX_RECENTS)
  };
}

export function readRecents(map: RecentBrandsMap, userId: string): BrandKey[] {
  return map[userId] ?? [];
}

export function isTableauUriPath(href: string): boolean {
  return /^\d+$/.test(getNextToLastUriSegment(href));
}

export function deriveInitials(user: UserModel): string {
  const name = user.getName?.() ?? '';
  const [first, second] = name.trim().split(/\s+/).filter(Boolean);
  if (first && second) {
    return (first.charAt(0) + second.charAt(0)).toUpperCase();
  }
  if (first && first.length >= 2) {
    return first.slice(0, 2).toUpperCase();
  }
  const username = user.getUsername?.() ?? '';
  return (username.slice(0, 2) || 'M').toUpperCase();
}

export function pathFromMenuItem(item: MenuItem): string {
  if (typeof item.routerLink === 'string') {
    return item.routerLink;
  }
  if (Array.isArray(item.routerLink)) {
    return (item.routerLink as unknown[]).map(String).join('/');
  }
  return item.url ?? '';
}

type MegaMeta = {
  iconName?: string;
  label?: string;
  description?: string;
};

export function toMegaMenuItems(
  items: MenuItem[],
  resolveMeta?: (item: MenuItem) => MegaMeta | undefined
): MegaMenuItem[] {
  return items.map((item) => {
    const meta = resolveMeta?.(item);
    const externalUrl =
      item.url && typeof item.routerLink !== 'string' ? item.url : undefined;
    return {
      id: item.id,
      iconName: meta?.iconName,
      label: meta?.label ?? item.label ?? '',
      description: meta?.description,
      escape: item.escape,
      routerLink: item.routerLink as string | unknown[] | undefined,
      href: externalUrl,
      target: item.target,
      command: externalUrl
        ? undefined
        : item.command
          ? (event: Event) => item.command?.({ originalEvent: event, item })
          : undefined,
      disabled: !!item.disabled,
      badge: item.badge ? { label: item.badge } : undefined
    };
  });
}

export function resolveMoreMeta(item: MenuItem): MegaMeta {
  const path = pathFromMenuItem(item);
  const label = item.label ?? '';
  const found = MORE_META.find(
    (entry) => entry.match.test(path) || entry.match.test(label)
  );
  return {
    iconName: found?.iconName ?? 'insights',
    description: found?.description ?? 'Open this dashboard.'
  };
}

export function resolveSettingsMeta(item: MenuItem): MegaMeta | undefined {
  const path = pathFromMenuItem(item);
  return SETTINGS_META.find(
    (entry) =>
      (entry.id !== undefined && entry.id === item.id) || entry.match.test(path)
  );
}

export function buildSupportMenu(
  user: UserModel | undefined,
  openFeedback: () => void
): MegaMenuItem[] {
  const items = [...supportBaseItems];
  if (user?.getUserType() === UserType.internal) {
    items.push(reportIssueItem(openFeedback));
  }
  return items;
}

export function prependMimSettingsItem(
  settingsMenuItems: MenuItem[],
  dashboards: DisplayDashboard[],
  brandProductPath: string
): MenuItem[] {
  const mim = dashboards.find(
    (d) =>
      d.literalId === NavbarConstants.ddiReportingRevenue ||
      d.literalId === NavbarConstants.ddiReportingConversion
  );
  if (!mim) {
    return settingsMenuItems;
  }
  return [
    {
      id: mim.literalId,
      label: mim.label,
      title: mim.label,
      url: `/a/${brandProductPath}/${mim.routerLinkPart}`
    },
    ...settingsMenuItems
  ];
}
